import { useEffect, useState } from 'react'
import { Alert, Button, Group, Modal, Stack, Text } from '@mantine/core'
import { IconRefresh } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  EVENTO_EXECUTOR_DESATUALIZADO, KNOB_EXECUTOR, atualizarExecutor, erroDaGestao, lerAjustes,
  lerVersaoDoExecutor, urlDoCluster, type ClusterCloudHsm,
} from '@/api/cloudhsm'
import { CHAVE_AJUSTES, CartaoDoPedido } from './AdicionarHsm'

/**
 * "Atualizar executor" (consoleai#26396): com o executor numa versão anterior à do catálogo (a
 * faixa da D1) ou com uma ação que voltou `executor_desatualizado`, o botão abre o pedido do
 * runner que leva a Lambda à imagem de `CLOUDHSM_EXECUTOR_IMAGE_URI`. As consultas usam as
 * MESMAS chaves da faixa (`VersaoDoExecutor`) e do "Adicionar HSM": nada é lido duas vezes.
 */
export function AtualizarExecutor({ tenantId, cluster, podeGerir }: {
  tenantId: number
  cluster: ClusterCloudHsm
  podeGerir: boolean
}) {
  const qc = useQueryClient()
  const chave = [CHAVE_AJUSTES, tenantId, cluster.cluster_id, cluster.regiao]
  const ajustes = useQuery({
    queryKey: chave,
    queryFn: () => lerAjustes(tenantId, cluster.cluster_id, cluster.regiao),
    retry: false,
  })
  const versao = useQuery({
    queryKey: ['cloudhsm', 'executor', tenantId, cluster.regiao, cluster.cluster_id],
    queryFn: () => lerVersaoDoExecutor(tenantId, cluster.cluster_id, cluster.regiao),
    retry: false,
    staleTime: 5 * 60_000,
  })
  const [desatualizadoVisto, setDesatualizadoVisto] = useState(false)
  const [aberto, setAberto] = useState(false)
  const [recemCriado, setRecemCriado] = useState<number | null>(null)

  useEffect(() => {
    const ouvir = (e: Event) => {
      const url = (e as CustomEvent<{ url?: string }>).detail?.url ?? ''
      if (urlDoCluster(url, tenantId, cluster.cluster_id)) setDesatualizadoVisto(true)
    }
    window.addEventListener(EVENTO_EXECUTOR_DESATUALIZADO, ouvir)
    return () => window.removeEventListener(EVENTO_EXECUTOR_DESATUALIZADO, ouvir)
  }, [tenantId, cluster.cluster_id])

  const pedir = useMutation({
    mutationFn: () => atualizarExecutor(tenantId, cluster.cluster_id, cluster.regiao),
    onSuccess: (pedidoId) => {
      setAberto(false)
      setRecemCriado(pedidoId)
      void qc.invalidateQueries({ queryKey: chave })
    },
  })

  const ajuste = ajustes.data?.knobs[KNOB_EXECUTOR]
  const ativo = ajustes.data?.pedido_ativo
  const pedidoId = recemCriado ?? (ativo?.knob === KNOB_EXECUTOR ? ativo.id : null)
  const precisa = versao.data?.situacao === 'antiga' || desatualizadoVisto
  if (!ajuste || (!precisa && pedidoId == null)) return null
  const imagem = typeof ajuste.para?.executor_image_uri === 'string' ? ajuste.para.executor_image_uri : null

  return (
    <Stack gap="xs" data-testid="atualizar-executor">
      {precisa && (ajuste.disponivel ? (
        <Group justify="space-between" wrap="nowrap">
          <Text size="sm">
            {desatualizadoVisto && versao.data?.situacao !== 'antiga'
              ? 'A última ação respondeu que o executor está desatualizado.'
              : 'O executor desta conta está numa versão anterior à do catálogo.'}
          </Text>
          <Button size="xs" leftSection={<IconRefresh size={14} />}
            disabled={!podeGerir || ativo != null}
            title={!podeGerir ? 'Exige a permissão cloudhsm:manage' : undefined}
            onClick={() => { pedir.reset(); setAberto(true) }}>
            Atualizar executor
          </Button>
        </Group>
      ) : (
        <Alert color="gray" title="Atualizar executor indisponível">{ajuste.motivo}</Alert>
      ))}

      {pedidoId != null && <CartaoDoPedido pedidoId={pedidoId} />}

      <Modal opened={aberto} onClose={() => setAberto(false)} title="Atualizar executor" centered>
        <Stack gap="sm">
          <Text size="sm">
            Abre um pedido no runner de Terraform que troca a imagem da Lambda do executor
            {imagem ? ` para ${imagem}` : ''}. Nada muda até o plan, a aprovação e o merge do MR.
          </Text>
          {pedir.isError && (
            <Alert color="red">{erroDaGestao(pedir.error, 'O pedido não foi aberto.')}</Alert>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setAberto(false)}>Cancelar</Button>
            <Button loading={pedir.isPending} onClick={() => pedir.mutate()}>Abrir o pedido</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}
